import { EventDispatcher } from './log'

const defaultHeartbeatArgs = {
  type: 'heartbeat',
  data: null
}

export class WebSocketClient extends EventDispatcher {
  // 心跳数据包
  heartbeatArgs = defaultHeartbeatArgs
  // #socket链接
  url = ''
  // #socket实例
  socket = null
  // #重连次数
  reconnectAttempts = 0
  // #最大重连数
  maxReconnectAttempts = 5
  // #重连间隔
  reconnectInterval = 10000 // 10 seconds
  // #发送心跳数据间隔
  heartbeatInterval = 1000 * 5
  // #计时器id
  heartbeatTimer = undefined
  // #彻底终止ws
  stopWs = false
  // *构造函数
  constructor(url, opt = {}) {
    const { heartbeatArgs = defaultHeartbeatArgs, heartbeatInterval = 1000 * 5 } = opt
    super()
    this.heartbeatArgs = heartbeatArgs
    this.heartbeatInterval = heartbeatInterval
    this.url = url

    this.connect()
  }
  // 存储外部注册的监听器
  externalListeners = {}

  // >生命周期钩子
  onopen(callBack) {
    this.externalListeners['open'] = this.externalListeners['open'] || []
    this.externalListeners['open'].push(callBack)
    this.addEventListener('open', callBack)
  }
  onmessage(callBack) {
    this.externalListeners['message'] = this.externalListeners['message'] || []
    this.externalListeners['message'].push(callBack)
    this.addEventListener('message', callBack)
  }
  onclose(callBack) {
    this.externalListeners['close'] = this.externalListeners['close'] || []
    this.externalListeners['close'].push(callBack)
    this.addEventListener('close', callBack)
  }
  onerror(callBack) {
    this.externalListeners['error'] = this.externalListeners['error'] || []
    this.externalListeners['error'].push(callBack)
    this.addEventListener('error', callBack)
  }
  // >消息发送
  send(message) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(message)
    } else {
      console.error('[WebSocket] 未连接')
    }
  }

  // !初始化连接
  connect() {
    if (this.reconnectAttempts === 0) {
      this.log('WebSocket', `初始化连接中...          ${this.url}`)
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return
    }
    this.socket = new WebSocket(this.url)

    // 重连时重新添加监听器
    this.restoreListeners()

    // !websocket连接成功
    this.socket.onopen = event => {
      this.stopWs = false
      // 重置重连尝试成功连接
      this.reconnectAttempts = 0
      // 在连接成功时停止当前的心跳检测并重新启动
      this.startHeartbeat()
      this.log('WebSocket', `连接成功,等待服务端数据推送[onopen]...     ${this.url}`)
      this.dispatchEvent('open', event)
    }

    this.socket.onmessage = event => {
      const parseMsg = msg => {
        if (typeof msg === 'string') {
          try {
            return JSON.parse(msg)
          } catch (error) {
            return msg
          }
        }
      }

      this.dispatchEvent('message', parseMsg(event?.data))
    }

    this.socket.onclose = event => {
      this.closeHeartbeat()
      if (this.reconnectAttempts === 0) {
        this.log(
          'WebSocket',
          `连接断开[onclose code=${event?.code || ''} reason=${event?.reason || ''}]...    ${
            this.url
          }`
        )
      }
      if (!this.stopWs) {
        this.handleReconnect()
      }
      this.dispatchEvent('close', event)
    }

    this.socket.onerror = event => {
      if (this.reconnectAttempts === 0) {
        this.log('WebSocket', `连接异常[onerror]...    ${this.url}`)
      }
      this.closeHeartbeat()
      this.dispatchEvent('error', event)
    }
  }

  // 重新添加监听器的方法
  restoreListeners() {
    for (const [type, listeners] of Object.entries(this.externalListeners)) {
      listeners.forEach(listener => {
        this.addEventListener(type, listener)
      })
    }
  }

  // > 断网重连逻辑
  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      this.log(
        'WebSocket',
        `尝试重连... (${this.reconnectAttempts}/${this.maxReconnectAttempts})       ${this.url}`
      )
      setTimeout(() => {
        this.connect()
      }, this.reconnectInterval)
    } else {
      this.closeHeartbeat()
      this.log('WebSocket', `最大重连失败，终止重连: ${this.url}`)
    }
  }

  // >关闭连接
  close() {
    if (this.socket) {
      this.stopWs = true
      this.socket.close()
      this.socket = null
      this.removeEventListener('open')
      this.removeEventListener('message')
      this.removeEventListener('close')
      this.removeEventListener('error')
    }
    this.closeHeartbeat()
  }

  // >开始心跳检测 -> 定时发送心跳消息
  startHeartbeat() {
    this.send(JSON.stringify(this.heartbeatArgs))

    if (this.stopWs) return
    if (this.heartbeatTimer) {
      this.closeHeartbeat()
    }

    this.heartbeatTimer = setInterval(() => {
      if (this.socket) {
        this.send(JSON.stringify(this.heartbeatArgs))
        this.log('WebSocket', '送心跳数据...')
      } else {
        console.error('[WebSocket] 未连接')
      }
    }, this.heartbeatInterval)
  }

  // >关闭心跳
  closeHeartbeat() {
    clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = undefined
  }
}
